import Slider from 'react-slick'
import { useRef, useEffect, useMemo } from 'react'
import AdventureCard from './AdventureCard'
import AdventureCardPlaceholder from './AdventureCardPlaceholder'
import ConservationSliderCard from './ConservationSliderCard'
import get from 'lodash/get'
import { trackGA4Ecommerce } from '../../gtm'
import PropTypes from 'prop-types'
import { settings } from '../dive-center/CardsDsl/ActivitySlider'
import { handleViewport } from 'react-in-viewport'
import useOtherActivities from './hooks/useOtherActivities'

const placeholderItems = [...new Array(4)]

function setImpressionList(item, index) {
  return {
    index: index + 1,
    ...item.viewItem,
  }
}

function AdventuresSlider({
  isConservation,
  inViewport,
  enterCount,
  forwardedRef,
  activityId,
  items,
}) {
  let sliderRef = useRef(null)
  const {
    otherActivities,
    isLoading,
    isShowPlaceholder,
    total,
    params,
    setParams,
    setIsShowPlaceholder,
    setIsLoading,
  } = useOtherActivities(activityId, inViewport, enterCount)

  const activities = useMemo(() => {
    return items || otherActivities
  }, [items, otherActivities])

  useEffect(() => {
    const numberOfItemsToShow = get(
      sliderRef,
      'current.innerSlider.props.slidesToShow',
    )
    const viewItem = get(activities, '[0].viewItem')
    if(numberOfItemsToShow && !isLoading && !isShowPlaceholder && viewItem) {
      trackGA4Ecommerce(
        'view_item_list',
        {
          currency: 'USD',
          items: activities.slice(0, numberOfItemsToShow).map(setImpressionList),
        },
        'PADI Travel - Adventures',
      )
      // scroll to first slide when user change tabs
      sliderRef.slickGoTo(0)
    }
    if(activities?.length) {
      setIsShowPlaceholder(false)
      setIsLoading(false)
    }
  }, [sliderRef, activities?.length, isLoading])

  /* eslint-disable react/prop-types */
  function beforeNextHandler(current, next) {
    if(!sliderRef?.props) { return }
    const { props } = sliderRef
    const lastSlide = props?.children?.length / props?.slidesToScroll
    const isTwoSlidesBefore = Math.abs(lastSlide - next / props?.slidesToScroll) <= 1
    const isShowNext = next > current
    if(isShowNext && isTwoSlidesBefore && props?.children?.length <= total) {
      sliderRef.slickGoTo(params.offset)
      setTimeout(() => {
        setParams((prevState) => ({ limit: 4, offset: prevState?.offset + prevState?.limit }))
      }, 500)
    }
    const viewItem = get(activities, '[0].viewItem')
    if(viewItem) {
      const difference = next + Math.abs(current - next)
      trackGA4Ecommerce(
        'view_item_list',
        {
          currency: 'USD',
          items: activities.slice(next, difference).map(setImpressionList),
        },
        'PADI Travel - Adventures',
      )
    }
  }
  /* eslint-enable react/prop-types */

  if(!inViewport && enterCount === 0) {
    return <div
      ref={forwardedRef}
    >
      {placeholderItems.map((_, i) => {
        return <AdventureCardPlaceholder key={i} />
      })}
    </div>
  }
  return (
    <div ref={forwardedRef} >
      <Slider
        ref={(slider) => (sliderRef = slider)}
        className={`slick-pt-custom same-height space-between-small ${
          isLoading ? 'placeholder-animation' : ''
        }`}
        {...settings(beforeNextHandler)}
      >
        { isShowPlaceholder
          ? placeholderItems.map((_, i) => {
            return <AdventureCardPlaceholder key={i} />
          })
          : activities.map((trip) => {
            return isConservation ? (
              <ConservationSliderCard
                adventure={trip}
                key={trip.activityId}
                isWidget={window.isWidget}
              />
            ) : (
              <AdventureCard
                adventure={trip}
                key={trip.id}
                isWidget={window.isWidget}
              />
            )
          })}
      </Slider>
    </div>
  )
}

AdventuresSlider.propTypes = {
  items: PropTypes.array,
  isLoading: PropTypes.bool,
  isConservation: PropTypes.bool,
  inViewport: PropTypes.bool,
  enterCount: PropTypes.number,
  forwardedRef: PropTypes.element,
  activityId: PropTypes.number,
  selectedCurrency: PropTypes.string,
}

export default handleViewport(AdventuresSlider, {}, { disconnectOnLeave: true })
